import React, { useState } from "react";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ProcuralWellBeing() {
  const [formData, setFormData] = useState({
    timeChallenge: "",
    distractedTimes: [],
    distractedOther: "",
    improvements: "",
    expectations: [],
    expectationsOther: "",
    extraHour: "",
    name: "",
    age: "",
    gender: "",
    role: "",
  });
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const distractedOptions = [
    "Right after starting the day",
    "Post-lunch",
    "During long meetings",
    "While switching between tasks",
    "End of the day",
  ];

  const expectationOptions = [
    "Tips to manage my time better",
    "Tools to stay focused",
    "Ways to reduce overwhelm",
    "Work-life balance strategies",
    "Increase daily productivity",
    "Motivation and mindset shift",
  ];

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters long";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = "Name can only contain letters and spaces";
    }

    // Age validation
    if (!formData.age) {
      newErrors.age = "Age is required";
    } else {
      const age = parseInt(formData.age);
      if (isNaN(age) || age < 16 || age > 30) {
        newErrors.age = "Age must be between 16 and 30";
      }
    }

    // Gender validation
    if (!formData.gender.trim()) {
      newErrors.gender = "Gender is required";
    } else if (formData.gender.trim().length < 2) {
      newErrors.gender = "Please provide a valid gender";
    }

    // Role validation
    if (!formData.role.trim()) {
      newErrors.role = "Role/Designation is required";
    } else if (formData.role.trim().length < 2) {
      newErrors.role = "Role must be at least 2 characters long";
    }

    // Time challenge validation
    if (!formData.timeChallenge.trim()) {
      newErrors.timeChallenge =
        "Please describe your biggest time management challenge";
    } else if (formData.timeChallenge.trim().length < 3) {
      newErrors.timeChallenge =
        "Please provide a more detailed response (at least 3 characters)";
    }

    // Distracted times validation
    if (formData.distractedTimes.length === 0) {
      newErrors.distractedTimes = "Please select at least one option";
    } else if (
      formData.distractedTimes.includes("Other") &&
      !formData.distractedOther.trim()
    ) {
      newErrors.distractedOther = "Please specify what 'Other' means";
    }

    // Improvements validation
    if (!formData.improvements.trim()) {
      newErrors.improvements = "Please describe what you'd like to improve";
    } else if (formData.improvements.trim().length < 3) {
      newErrors.improvements =
        "Please provide a more detailed response (at least 3 characters)";
    }

    // Expectations validation
    if (formData.expectations.length === 0) {
      newErrors.expectations = "Please select at least one expectation";
    } else if (
      formData.expectations.includes("Other") &&
      !formData.expectationsOther.trim()
    ) {
      newErrors.expectationsOther = "Please specify what 'Other' means";
    }

    // Extra hour validation
    if (!formData.extraHour.trim()) {
      newErrors.extraHour = "Please describe how you'd use an extra hour";
    } else if (formData.extraHour.trim().length < 3) {
      newErrors.extraHour =
        "Please provide a more detailed response (at least 3 characters)";
    }

    return newErrors;
  };

  const handleCheckboxChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value],
    }));

    // Clear related errors when user makes a selection
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.querySelector(
        `[data-field="${firstErrorField}"]`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsLoading(true);
    setErrors({});

    const formData1 = new URLSearchParams();
    formData1.append("timeChallenge", formData.timeChallenge);
    formData1.append(
      "distractedTimes",
      formData.distractedTimes.join(", ") + " " + formData.distractedOther
    );
    formData1.append("improvements", formData.improvements);
    formData1.append(
      "expectations",
      formData.expectations.join(", ") + " " + formData.expectationsOther
    );
    formData1.append("extraHour", formData.extraHour);
    formData1.append("companyName", "Procural");
    formData1.append("name", formData.name);
    formData1.append("age", formData.age);
    formData1.append("gender", formData.gender);
    formData1.append("role", formData.role);

    try {
      const response = await fetch(
        "https://script.google.com/macros/s/AKfycbx9d-fiyj8s0R7TG-EEAkPcrIZjS0JsPmMRXD-ah4z0ws70Hh3ysVtJs-ntvGiosaoA/exec",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData1,
        }
      );

      const data = await response.json();
      setIsLoading(false);
      setIsSuccess(true);
      console.log("Success:", data);
    } catch (error) {
      setIsLoading(false);
      setIsSuccess(true);
      console.error("Fetch error:", error);
    }
  };

  const handleClickHome = () => {
    // setIsSuccess(false);
    // setErrors({});
    // setFormData({
    //   timeChallenge: "",
    //   distractedTimes: [],
    //   distractedOther: "",
    //   improvements: "",
    //   expectations: [],
    //   expectationsOther: "",
    //   extraHour: "",
    //   name: "",
    //   age: "",
    //   gender: "",
    //   role: "",
    // });
    navigate("/");
  };

  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div className="flex items-center mt-2 text-red-600 text-sm">
        <AlertCircle className="w-4 h-4 mr-1" />
        {error}
      </div>
    );
  };

  const getInputClassName = (fieldName) => {
    const baseClass =
      "w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent";
    return errors[fieldName]
      ? `${baseClass} border-red-500 focus:ring-red-500`
      : `${baseClass} border-gray-300`;
  };

  const getTextareaClassName = (fieldName) => {
    const baseClass =
      "w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none";
    return errors[fieldName]
      ? `${baseClass} border-red-500 focus:ring-red-500`
      : `${baseClass} border-gray-300`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Submitting your feedback...
          </h2>
          <p className="text-gray-600">
            Please wait while we process your responses
          </p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-30 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your feedback has been successfully submitted. We appreciate you
            taking the time to share your thoughts with us.
          </p>
          <button
            onClick={handleClickHome}
            className="w-full bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center"
          >
            Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid min-h-screen">
          {/* Banner Image Section */}
          <div className="relative bg-gray-200 flex items-center justify-center">
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-700">
              <div className="text-center text-white p-8">
                <div className="flex flex-col lg:flex-row items-center text-sm">
                  <img
                    src="/images/procural-banner.png"
                    alt="Tell Us What You Think Banner"
                    className="w-[300px] lg:w-[400px] mt-4"
                  />
                  <div className="mt-6 lg:ml-8 flex flex-col justify-evenly items-center text-center lg:text-left flex-wrap">
                    <h1 className="text-3xl lg:text-4xl font-bold mb-4">
                      Tell Us What You Think
                    </h1>
                    <p className="text-lg lg:text-xl opacity-90 max-w-md">
                      Help us understand your work challenges and create better
                      solutions for you
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="bg-white p-8 lg:p-12 overflow-y-auto">
            <div className="space-y-8">
              <div data-field="name">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  1. Your Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={getInputClassName("name")}
                  placeholder="Please specify your name..."
                />
                <ErrorMessage error={errors.name} />
              </div>

              <div data-field="age">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  2. Your Age *
                </label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleInputChange("age", e.target.value)}
                  className={getInputClassName("age")}
                  placeholder="Please specify your age..."
                  min="16"
                  max="30"
                />
                <ErrorMessage error={errors.age} />
              </div>

              <div data-field="gender">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  3. Your Gender *
                </label>
                <input
                  type="text"
                  value={formData.gender}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  className={getInputClassName("gender")}
                  placeholder="Please specify your gender (male/female/other)"
                />
                <ErrorMessage error={errors.gender} />
              </div>

              <div data-field="role">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  4. Your Role/Designation *
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                  className={getInputClassName("role")}
                  placeholder="Please specify your Role or Designation"
                />
                <ErrorMessage error={errors.role} />
              </div>

              <div data-field="timeChallenge">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  5. What is the biggest challenge you face in managing your
                  time at work? *
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  (e.g., too many meetings, lack of focus, procrastination,
                  unrealistic deadlines, etc.)
                </p>
                <textarea
                  value={formData.timeChallenge}
                  onChange={(e) =>
                    handleInputChange("timeChallenge", e.target.value)
                  }
                  className={getTextareaClassName("timeChallenge")}
                  rows="4"
                  placeholder="Share your biggest time management challenge..."
                />
                <ErrorMessage error={errors.timeChallenge} />
              </div>

              <div data-field="distractedTimes">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  6. When do you feel the most distracted or drained during your
                  workday? *
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  (Select all that apply)
                </p>
                <div className="space-y-3">
                  {distractedOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center space-x-3 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.distractedTimes.includes(option)}
                        onChange={() =>
                          handleCheckboxChange("distractedTimes", option)
                        }
                        className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.distractedTimes.includes("Other")}
                      onChange={() =>
                        handleCheckboxChange("distractedTimes", "Other")
                      }
                      className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">Other:</span>
                    <input
                      type="text"
                      value={formData.distractedOther}
                      onChange={(e) =>
                        handleInputChange("distractedOther", e.target.value)
                      }
                      className={getInputClassName("distractedOther")}
                      placeholder="Please specify..."
                    />
                  </div>
                </div>
                <ErrorMessage error={errors.distractedTimes} />
                <ErrorMessage error={errors.distractedOther} />
              </div>

              <div data-field="improvements">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  7. What would you like to improve in your daily work routine?
                  *
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  (e.g., better focus, time planning, reducing stress, finishing
                  work on time, etc.)
                </p>
                <textarea
                  value={formData.improvements}
                  onChange={(e) =>
                    handleInputChange("improvements", e.target.value)
                  }
                  className={getTextareaClassName("improvements")}
                  rows="4"
                  placeholder="What improvements would make the biggest difference..."
                />
                <ErrorMessage error={errors.improvements} />
              </div>

              <div data-field="expectations">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  8. What do you expect to gain from this session? *
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  (Select all that apply)
                </p>
                <div className="space-y-3">
                  {expectationOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center space-x-3 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.expectations.includes(option)}
                        onChange={() =>
                          handleCheckboxChange("expectations", option)
                        }
                        className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.expectations.includes("Other")}
                      onChange={() =>
                        handleCheckboxChange("expectations", "Other")
                      }
                      className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">Other:</span>
                    <input
                      type="text"
                      value={formData.expectationsOther}
                      onChange={(e) =>
                        handleInputChange("expectationsOther", e.target.value)
                      }
                      className={getInputClassName("expectationsOther")}
                      placeholder="Please specify..."
                    />
                  </div>
                </div>
                <ErrorMessage error={errors.expectations} />
                <ErrorMessage error={errors.expectationsOther} />
              </div>

              <div data-field="extraHour">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  9. If you had one extra hour every day, how would you ideally
                  use it? *
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  (e.g., finish pending work, take breaks, plan better, learn
                  something new, rest, spend time with family, etc.)
                </p>
                <textarea
                  value={formData.extraHour}
                  onChange={(e) =>
                    handleInputChange("extraHour", e.target.value)
                  }
                  className={getTextareaClassName("extraHour")}
                  rows="4"
                  placeholder="How would you use that precious extra hour..."
                />
                <ErrorMessage error={errors.extraHour} />
              </div>

              <div className="pt-6">
                <button
                  onClick={handleSubmit}
                  className="w-full bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center"
                >
                  Submit Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
